import React, { useEffect, useState, useRef } from "react";
import { useTransactions } from "./useTransactions";
import "../css/FrameStyles.css";
import loadingAnimation from "../assets/Loading.json";
import Lottie from "lottie-react";
import CapitalSummaryCard from "./CapitalSummaryCard";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import cloneDeep from "lodash/cloneDeep";
import axios from "axios";
import { fetchTaxRates } from '../utils/taxRatesCache';

function CapitalLossTransactionsFrame({ username }) {
  const backendUrl = "https://profitree-backend-9482feeffc4a.herokuapp.com";
  const isMobileView = window.innerWidth <= 768;
  const tableRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [transactionTypes] = useState([
    { name: "SELL", value: "SELL" },
    { name: "OPTION", value: "OPTION" },
  ]);
  const [taxTypes] = useState([
    { name: "Long Term (15%)", value: "Long Term(15%)" },
    { name: "Short Term (30%)", value: "Short Term(30%)" },
    { name: "OPTION(30%)", value: "OPTION(30%)" },
  ]);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.IN },
    taxType: { value: null, matchMode: FilterMatchMode.IN },
  });

  const toNumeric = (value) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === "string") {
      const numeric = Number(value.replace(/[^0-9.-]/g, ""));
      return Number.isFinite(numeric) ? numeric : 0;
    }
    return 0;
  };

  const formatCurrencyValue = (amount) => {
    const numeric = toNumeric(amount);
    const formatted = Math.abs(numeric).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return numeric < 0 ? `-$${formatted}` : `$${formatted}`;
  };

  const formatQuantityDisplay = (qty) => {
    const numeric = toNumeric(qty);
    const fixed = Number(numeric.toFixed(2));
    if (Number.isInteger(fixed)) {
      return fixed.toString();
    }
    return fixed.toFixed(2).replace(/\.?0+$/, "");
  };

  const renderTickerCell = (rowData) => {
    const ticker = (rowData.tickerSymbol || rowData.ticker || "--").toString().trim();
    return <span className="table-highlight-text table-highlight-text--loss">{ticker || "--"}</span>;
  };

  const renderSoldColumn = (rowData) => {
    const priceValue =
      rowData.salesPriceValue !== undefined
        ? rowData.salesPriceValue
        : rowData.soldPriceValue !== undefined
        ? rowData.soldPriceValue
        : Number(rowData.price);
    const quantityValue = toNumeric(rowData.quantity);
    const priceLabel = formatCurrencyValue(priceValue);
    const quantityLabel = formatQuantityDisplay(quantityValue);

    return (
      <span className="table-value-with-annotation">
        {priceLabel}
        <span className="table-annotation">
          (
          <span
            className="table-tooltip-trigger loss-remaining-quantity-tooltip"
            data-pr-tooltip={`Sold quantity: ${quantityLabel}`}
            title={`Sold quantity: ${quantityLabel}`}
          >
            {quantityLabel}
          </span>
          )
        </span>
      </span>
    );
  };

  const renderLossCell = (rowData) => {
    const lossValue = -Math.abs(toNumeric(rowData.profitLossValue ?? rowData.profitLoss));
    const displayValue = formatCurrencyValue(lossValue);
    const rawTaxType = rowData.taxType || "";
    const descriptorMatch = rawTaxType.match(/^[A-Za-z\s]+/);
    const descriptor = descriptorMatch ? descriptorMatch[0].trim() : rawTaxType;
    const abbreviation = descriptor ? descriptor.charAt(0).toUpperCase() : "–";
    const rateMatch = rawTaxType.match(/(\d+(\.\d+)?)%/);
    const rateLabel = rateMatch ? `${rateMatch[1]}%` : "";
    const tooltipText = descriptor
      ? `${descriptor}${rateLabel ? ` at ${rateLabel} tax rate` : ""}`
      : "Tax type unavailable";

    return (
      <span className="table-highlight-text table-highlight-text--loss">
        <span className="table-value-with-annotation">
          {displayValue}
          <span className="table-annotation">
            (
            <span
              className="table-tooltip-trigger loss-tax-type-tooltip"
              data-pr-tooltip={tooltipText}
              title={tooltipText}
            >
              {abbreviation}
            </span>
            )
          </span>
        </span>
      </span>
    );
  };

  const renderTaxSavingsCell = (rowData) => {
    const taxValue = toNumeric(rowData.taxesOwed);
    const displayValue = formatCurrencyValue(taxValue);
    const rawTaxType = rowData.taxType || "";
    const descriptorMatch = rawTaxType.match(/^[A-Za-z\s]+/);
    const descriptor = descriptorMatch ? descriptorMatch[0].trim() : rawTaxType;
    const abbreviation = descriptor ? descriptor.charAt(0).toUpperCase() : "–";
    const rateMatch = rawTaxType.match(/(\d+(\.\d+)?)%/);
    const rateLabel = rateMatch ? `${rateMatch[1]}%` : "";
    const tooltipText = descriptor
      ? `${descriptor}${rateLabel ? ` at ${rateLabel} tax rate` : ""}`
      : "Tax type unavailable";

    return (
      <span className="table-highlight-text table-highlight-text--loss">
        <span className="table-value-with-annotation">
          {displayValue}
          <span className="table-annotation">
            (
            <span
              className="table-tooltip-trigger loss-tax-type-tooltip"
              data-pr-tooltip={tooltipText}
              title={tooltipText}
            >
              {abbreviation}
            </span>
            )
          </span>
        </span>
      </span>
    );
  };
  const [yearFilter, setYearFilter] = useState(
    new Date().getFullYear().toString()
  );

  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [expandedRows, setExpandedRows] = useState(null);

  const { data, loading, error } = useTransactions(
    "transactions/getCurrentYearTaxableLoss"
  );

  // Ensure error is defined
  const currentError = error || null;

  const [totalTaxSavings, setTotalTaxSavings] = useState("");
  const [totalCapitalLoss, setTotalCapitalLoss] = useState("");
  const [shortTermRealizedLoss, setShortTermRealizedLoss] = useState("");
  const [longTermRealizedLoss, setLongTermRealizedLoss] = useState("");
  const [longTermTaxRate, setLongTermTaxRate] = useState(20); // Default value
  const [shortTermTaxRate, setShortTermTaxRate] = useState(30); // Default value

  // Fetch tax rates from backend
  const getTaxRates = async () => {
    if (!username) return;
    const rates = await fetchTaxRates(username);
    if (rates) {
      setLongTermTaxRate(rates.LONG_TERM);
      setShortTermTaxRate(rates.SHORT_TERM);
    }
  };

  // Utility to format numbers in a string to 2 decimals
  function formatNumbersInString(str) {
    if (!str) return str;
    return str.replace(/(\d+\.\d{2})\d*/g, (match) => Number(match).toFixed(2));
  }

  const getSummaryCardsValue = (data = tableData) => {
    // Calculate total taxes owed and total capital gain
    setTotalTaxSavings(
      data.reduce((acc, transaction) => {
        const taxType = transaction.capitalGainTaxType;
        const taxRate =
          taxType === "LONG" ? longTermTaxRate / 100 : shortTermTaxRate / 100;
        return acc + Math.abs(transaction.profitLossWithoutPrefix * taxRate);
      }, 0).toFixed(2)
    );

    setTotalCapitalLoss(
      data.reduce((acc, transaction) => {
        return acc + Math.abs(transaction.profitLossWithoutPrefix);
      }, 0).toFixed(2)
    );
    setShortTermRealizedLoss(
              data.reduce((acc, transaction) => {
                const taxType = transaction.capitalGainTaxType;
                console.log(taxType);
                if(taxType === "SHORT" || taxType === null || taxType === undefined){
                    console.log(transaction.profitLossWithoutPrefix);
                    acc = acc + Math.abs(transaction.profitLossWithoutPrefix);
                }
                return acc;
              }, 0).toFixed(2)
            );

        setLongTermRealizedLoss(
              data.reduce((acc, transaction) => {
                const taxType = transaction.capitalGainTaxType;
                if(taxType === "LONG"){
                    acc = acc + Math.abs(transaction.profitLossWithoutPrefix);
                }
                return acc;
              }, 0).toFixed(2)
            );
  };



  useEffect(() => {
    const fetchAndProcessData = async () => {
      if (username) {
        await getTaxRates(); // Ensure tax rates are fetched first
      }

      if (!loading && currentError === null) {
        let tempTableData = cloneDeep(data);

        // Apply Year Filter
        if (yearFilter) {
          tempTableData = tempTableData.filter(
            (item) => item.date.split("-")[0] === yearFilter
          );
        }

        const processedData = tempTableData.map((item) => {
          const tempItem = cloneDeep(item);

          tempItem.purchaseDate =
            item.settlementTransactions.length > 1
              ? "Multiple"
              : item.settlementTransactions.length === 1
              ? item.settlementTransactions[0].date
              : item.date;

          const purchasePriceValue = item.settlementTransactions.length > 0
            ? (
                item.settlementTransactions.reduce(
                  (acc, transaction) =>
                    acc + transaction.price * transaction.settledQuantityForCurrentTransaction,
                  0
                ) /
                item.settlementTransactions.reduce(
                  (acc, transaction) => acc + transaction.settledQuantityForCurrentTransaction,
                  0
                )
              )
            : 0;
          tempItem.purchasePriceValue = purchasePriceValue;
          tempItem.purchasePrice = formatCurrencyValue(purchasePriceValue);

          tempItem.taxType =
            item.capitalGainTaxType === "LONG"
              ? formatNumbersInString(`Long Term (${Number(longTermTaxRate).toFixed(2)}%)`)
              : item.capitalGainTaxType === "SHORT"
              ? formatNumbersInString(`Short Term (${Number(shortTermTaxRate).toFixed(2)}%)`)
              : formatNumbersInString(`Short Term (${Number(shortTermTaxRate).toFixed(2)}%)`);

          const taxRate =
            item.capitalGainTaxType === "LONG"
              ? longTermTaxRate
              : shortTermTaxRate;
          const taxesOwedValue = item.profitLoss * (taxRate / 100);
          tempItem.taxesOwed = formatCurrencyValue(taxesOwedValue);
          tempItem.taxesOwedValue = taxesOwedValue;

          const salesPriceValue =
            item.salesPrice !== undefined
              ? Number(item.salesPrice)
              : item.soldPrice !== undefined
              ? Number(item.soldPrice)
              : Number(item.price);
          tempItem.salesPriceValue = salesPriceValue;
          tempItem.price = formatCurrencyValue(salesPriceValue);

          tempItem.quantity = toNumeric(item.quantity);

          const lossValue = toNumeric(item.profitLoss);
          tempItem.profitLossWithoutPrefix = lossValue;
          tempItem.profitLossValue = lossValue;
          tempItem.profitLoss = formatCurrencyValue(lossValue);

          // Format description numbers
          tempItem.description = formatNumbersInString(item.description);

          return tempItem;
        });

        setTableData(processedData);
        getSummaryCardsValue(processedData); // Update summary values based on new table data
      }
    };

    fetchAndProcessData(); // Call the function
  }, [data, yearFilter, username, longTermTaxRate, shortTermTaxRate]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Lottie
          animationData={loadingAnimation}
          style={{ height: "150px", width: "150px" }} // Adjust loader size
        />
      </div>
    );
  }

  if (currentError) {
    return (
      <div>
        Error: {currentError}, please contact support for help:
        support@profitree-tax.com
      </div>
    );
  }

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const clearFilter = () => {
    initFilters();
  };

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      type: { value: null, matchMode: FilterMatchMode.IN },
      taxType: { value: null, matchMode: FilterMatchMode.IN },
    });
    setGlobalFilterValue("");
    setGlobalFilterValue("");
    setYearFilter("");
    if (tableRef.current) {
      tableRef.current.reset();
    }
  };

  const renderHeader = () => {
    return (
      <div className="datatable-header-wrapper">
        <div className="datatable-filters-wrapper">
          <label>
            Year
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <option value="">All</option>
              {[
                ...new Set([
                  ...data.map((d) => d.date.split("-")[0]),
                  new Date().getFullYear().toString(), // Ensure the current year is always included
                ]),
              ]
                .sort((a, b) => b - a)
                .map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <IconField>
          <Button
            type="button"
            icon="pi pi-filter-slash"
            label="Clear Filters"
            outlined
            onClick={clearFilter}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            x="0px"
            y="0px"
            width="100"
            height="100"
            viewBox="0 0 50 50"
          >
            <path d="M 21 3 C 11.622998 3 4 10.623005 4 20 C 4 29.376995 11.622998 37 21 37 C 24.712383 37 28.139151 35.791079 30.9375 33.765625 L 44.085938 46.914062 L 46.914062 44.085938 L 33.886719 31.058594 C 36.443536 28.083 38 24.223631 38 20 C 38 10.623005 30.377002 3 21 3 z M 21 5 C 29.296122 5 36 11.703883 36 20 C 36 28.296117 29.296122 35 21 35 C 12.703878 35 6 28.296117 6 20 C 6 11.703883 12.703878 5 21 5 z"></path>
          </svg>
          <InputText
            value={globalFilterValue || ""}
            onChange={onGlobalFilterChange}
            placeholder="Search by keyword"
          />
        </IconField>
      </div>
    );
  };

  const itemTemplate = (option) => {
    return (
      <div className="flex align-items-center gap-2">
        <span>{option.name}</span>
      </div>
    );
  };

  const transactionTypeRowFilterTemplate = (options) => {
    return (
      <MultiSelect
        value={options.value}
        options={transactionTypes}
        itemTemplate={itemTemplate}
        onChange={(e) => options.filterApplyCallback(e.value)}
        optionLabel="name"
        placeholder="Any"
        className="p-column-filter"
        maxSelectedLabels={1}
      />
    );
  };

  const taxTypeRowFilterTemplate = (options) => {
    return (
      <MultiSelect
        value={options.value}
        options={taxTypes}
        itemTemplate={itemTemplate}
        onChange={(e) => options.filterApplyCallback(e.value)}
        optionLabel="name"
        placeholder="Any"
        className="p-column-filter"
        maxSelectedLabels={1}
      />
    );
  };

  const allowExpansion = (rowData) => {
    return rowData.settlementTransactions.length > 0 || isMobileView;
  };

  const transactionTypeBodyTemplate = (rowData) => {
    const base = rowData.type;
    let extra = "";
    //if (base === "OPTION" || base === "DIVIDEND") {
    if (rowData.description) {
      extra = `(${rowData.description})`;
    }
    if (rowData.optionType && rowData.optionSide && rowData.description) {
      extra = `(${rowData.optionType} ${rowData.optionSide}) - (${rowData.description})`;
    } else if (rowData.optionType && rowData.description) {
      extra = `(${rowData.optionType}) - (${rowData.description})`;
    } else if (rowData.description) {
      extra = `(${rowData.description})`;
    }

    return (
      <div>
        <div>{base}</div>
        {extra && <small style={{ color: "#666" }}>{extra}</small>}
      </div>
    );
    // }

    return <div>{base}</div>;
  };

  const rowExpansionTemplate = (data) => {
    return !isMobileView ? (
      <div className="p-3">
        <h5>Settlement info</h5>
        <DataTable value={data.settlementTransactions}>
          <Column field="instrument_id" header="Ticker" sortable></Column>
          <Column
            field="type"
            header="Transaction Type"
            style={{ maxWidth: "15rem", minWidth: "15rem" }}
            sortable
            body={transactionTypeBodyTemplate}
          ></Column>
          <Column field="costBasis" header="Purchase Price" sortable></Column>
          <Column
            field="quantity"
            header="Purchase Quantity"
            sortable
          ></Column>
          <Column
            field="settledQuantityForCurrentTransaction"
            header="Settled Quantity(Current Tx)"
            sortable
          ></Column>
          <Column field="date" header="Purchase Date" sortable></Column>
        </DataTable>
      </div>
    ) : (
      <div className="mobile-expand-info">
        <div className="expand-row">
          <h4>Account Name:</h4>
          <p>{data.accountName}</p>
        </div>
        <div className="expand-row">
          <h4>Ticker:</h4>
          <p>{data.tickerSymbol}</p>
        </div>
        <div className="expand-row">
          <h4>Transaction Type:</h4>
          <p>{data.type}</p>
        </div>

        <div className="expand-row">
          <h4>Purchase Date:</h4>
          <p>{data.purchaseDate}</p>
        </div>
        <div className="expand-row">
          <h4>Purchase Price:</h4>
          <p>{data.purchasePrice}</p>
        </div>
        <div className="expand-row">
          <h4>Sold Quantity:</h4>
          <p>{data.quantity}</p>
        </div>
        <div className="expand-row">
          <h4>Sold Date:</h4>
          <p>{data.date}</p>
        </div>
        <div className="expand-row">
          <h4>Sold Price:</h4>
          <p>{data.price}</p>
        </div>
        <div className="expand-row">
          <h4>Loss Amt:</h4>
          <p>{data.profitLoss}</p>
        </div>
        <div className="expand-row">
          <h4>Tax Type:</h4>
          <p>{data.taxType}</p>
        </div>
        <div className="expand-row">
          <h4>Taxes Offset:</h4>
          <p>{data.taxesOwed}</p>
        </div>
        <div className="expand-row">
          <h4>Description:</h4>
          <p>{data.description}</p>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="capital-summary-container">
        <CapitalSummaryCard
          title="Realized Short-term Losses"
          value={shortTermRealizedLoss}
          type="negative"
          tooltip="Losses from assets held for less than a year, can be used to offset short-term gains"
        />
        <CapitalSummaryCard
          title="Realized Long-term Losses"
          value={longTermRealizedLoss}
          type="negative"
          tooltip="Losses from assets held for more than a year, can be used to offset long-term gains"
        />
        <CapitalSummaryCard
          title="Total Taxes Offset by Losses"
          value={totalTaxSavings}
          type="positive"
          tooltip="Estimated tax savings from your capital losses based on your tax rates"
        />
      </div>

      <Tooltip
        target=".loss-remaining-quantity-tooltip"
        position="top"
        showDelay={100}
        hideDelay={100}
      />
      <Tooltip
        target=".loss-tax-type-tooltip"
        position="top"
        showDelay={100}
        hideDelay={100}
      />

      <DataTable
        value={tableData}
        removableSort
        sortMode="multiple"
        paginator
        rows={10}
        dataKey="id"
        filters={filters}
        loading={loading}
        ref={tableRef}
        multiSortMeta={[
          {
            field: "taxesOwedValue",
            order: "1",
          },
        ]}
        globalFilterFields={[
          "accountName",
          "tickerSymbol",
          "type",
          "purchaseDate",
          "date",
          "taxType",
        ]}
        header={renderHeader()}
        emptyMessage="No transactions found."
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        onValueChange={(e) => {
          getSummaryCardsValue(e);
        }}
      >
        <Column
          expander={allowExpansion}
          style={{ width: "3rem" }}
          className={isMobileView ? "mobile-row-expander" : ""}
        />
        {!isMobileView ? (
          <Column field="accountName" header="Account Name" sortable></Column>
        ) : null}
        <Column
          field="tickerSymbol"
          header="Ticker"
          sortable
          body={renderTickerCell}
          className="table-highlight-column-cell"
        ></Column>
        {!isMobileView ? (
          <Column
            field={"type"}
            header="Transaction Type"
            sortable
            showFilterMenu={true}
            style={{ maxWidth: "10rem", minWidth: "10rem" }}
            filter
            filterField="type"
            showFilterMatchModes={false}
            filterElement={transactionTypeRowFilterTemplate}
            filterMenuStyle={{ width: "14rem" }}
            body={transactionTypeBodyTemplate}
          ></Column>
        ) : null}
        {!isMobileView ? (
          <Column field="purchaseDate" header="Purchase Date" sortable></Column>
        ) : null}
        {!isMobileView ? (
          <Column
            field="purchasePrice"
            header="Purchase Price"
            sortable
            sortField="purchasePriceValue"
          ></Column>
        ) : null}
        {!isMobileView ? (
          <Column
            field="salesPriceValue"
            header="Sold Price / Quantity"
            sortable
            body={renderSoldColumn}
            sortField="salesPriceValue"
          ></Column>
        ) : null}
        {!isMobileView ? (
          <Column field="date" header="Sold Date" sortable></Column>
        ) : null}
        <Column
          field="profitLossValue"
          header="Loss Amt"
          sortable
          body={renderLossCell}
          className="table-highlight-column-cell"
        ></Column>
        <Column
          field="taxesOwedValue"
          header="Tax Offset"
          sortable
          body={renderTaxSavingsCell}
          className="table-highlight-column-cell"
        ></Column>
      </DataTable>
    </div>
  );
}

export default CapitalLossTransactionsFrame;
